import { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Calendar, MapPin, Users, Trophy, LogIn, LogOut, ArrowRight } from "lucide-react";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { useSearchParams, Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import PlayerNav from "@/components/PlayerNav";
import EventCard from "@/components/EventCard";
import MyEventCard from "@/components/MyEventCard";
import { MyEventCardHorizontal } from "@/components/profile/MyEventCardHorizontal";
import FeaturedEventsSection from "@/components/FeaturedEventsSection";
import SuggestedClubsSection from "@/components/SuggestedClubsSection";

import ExploreEvents from "@/components/ExploreEvents";
import ExploreClubs from "@/components/ExploreClubs";
import ExplorePlayers from "@/components/ExplorePlayers";
import NotificationsAndChats from "@/components/NotificationsAndChats";
import PartnerInvitationConfirmation from "@/components/PartnerInvitationConfirmation";
import EventRegistrationForm from "@/components/EventRegistrationForm";
import PaymentProcess from "@/components/PaymentProcess";
import ChatScreen from "@/components/ChatScreen";
import SportSelector from "@/components/SportSelector";
import ExploreLeagues from "@/components/ExploreLeagues";
import FeaturedOrganizersSection from "@/components/FeaturedOrganizersSection";
import OrganizerProfile from "@/components/OrganizerProfile";
import exploreTorneosImg from "@/assets/explore-torneos.jpg";
import exploreLigasImg from "@/assets/explore-ligas.jpg";
import exploreOrganizadoresImg from "@/assets/explore-organizadores.jpg";
import exploreJugadoresImg from "@/assets/explore-jugadores.jpg";

const exploreHeaderImages: Record<string, string> = {
  events: exploreTorneosImg,
  leagues: exploreLigasImg,
  organizers: exploreOrganizadoresImg,
  players: exploreJugadoresImg,
};
import ExploreOrganizers from "@/components/ExploreOrganizers";
import ProfileContent from "@/components/ProfileContent";
import { FollowingListDialog } from "@/components/profile/FollowingListDialog";
import DatabasePopulator from "@/components/DatabasePopulator";
import UpcomingEventPreview from "@/components/UpcomingEventPreview";
import EmbeddedAuth from "@/components/EmbeddedAuth";
import { useEvents } from "@/hooks/useEvents";
import { useClubs } from "@/hooks/useClubs";
import { useFeaturedOrganizers, useOrganizers } from "@/hooks/useOrganizers";
import { useMyEvents } from "@/hooks/useMyEvents";
import { useUserTournamentsCount } from "@/hooks/useUserTournamentsCount";
import { useUserFollowingCount } from "@/hooks/useUserFollowingCount";
import {usePlatform} from "@/hooks/usePlatform.ts";

const Index = () => {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState("dashboard");
  const [myEventsTab, setMyEventsTab] = useState("upcoming");
  const [exploreTab, setExploreTab] = useState("events");
  const [currentScreen, setCurrentScreen] = useState("main");
  const [screenData, setScreenData] = useState<any>(null);
  const [navigationHistory, setNavigationHistory] = useState<Array<{screen: string, data: any}>>([
    { screen: "main", data: null }
  ]);
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [userProfile, setUserProfile] = useState<any>(null);
  const { events, loading: eventsLoading } = useEvents();
  const { clubs, loading: clubsLoading } = useClubs();
  const { organizers, loading: organizersLoading } = useFeaturedOrganizers();
  const { organizers: allOrganizers, loading: allOrganizersLoading } = useOrganizers();
  const { myEvents, loading: myEventsLoading, refetch: refetchMyEvents } = useMyEvents();
  const { tournamentsCount } = useUserTournamentsCount();
  const { followingCount } = useUserFollowingCount();
  const [hasNavigationState, setHasNavigationState] = useState(false);
  const [hideNotificationBadge, setHideNotificationBadge] = useState(false);
  const [friendsDialogOpen, setFriendsDialogOpen] = useState(false);


  // New state for sport and level selection - Default to padel
  const [selectedSport, setSelectedSport] = useState("Pádel");
  
  
  // New state for tournament/league filter
  const [showTournaments, setShowTournaments] = useState(true); // true = tournaments, false = leagues

  // Handle state from navigation (e.g., coming back from profile or post-payment)
  useEffect(() => {
    if (location.state?.activeTab || location.state?.exploreTab || location.state?.currentScreen) {
      console.log('=== Index - Received Navigation State ===');
      console.log('Requested tab:', location.state.activeTab);
      console.log('Current user:', user?.id, user?.email);
      console.log('Force refresh:', location.state?.forceRefresh);
      
      // Si se pide "my-events" pero no hay usuario aún, esperar
      if (location.state.activeTab === 'my-events' && !user) {
        console.log('⏳ Waiting for user to be available before navigating to my-events...');
        return; // No limpiar el estado, esperar al siguiente render con user
      }
      
      setHasNavigationState(true);
      
      if (location.state?.activeTab) {
        // Forzar el tab solicitado
        setActiveTab(location.state.activeTab);
        console.log('✅ Tab set to:', location.state.activeTab);
      }
      if (location.state?.exploreTab) {
        setExploreTab(location.state.exploreTab);
      }
      if (location.state?.currentScreen) {
        setCurrentScreen(location.state.currentScreen);
      } else {
        // Asegurarse de volver a la pantalla principal
        setCurrentScreen('main');
      }
      if (location.state?.screenData) {
        setScreenData(location.state.screenData);
      }
      
      // Si hay forceRefresh, refrescar myEvents
      if (location.state?.forceRefresh && refetchMyEvents) {
        console.log('🔄 Force refreshing myEvents...');
        refetchMyEvents();
      }

      // Clear navigation state IMMEDIATELY after using it
      navigate(location.pathname + location.search, { replace: true, state: null });
    }
  }, [location.state, user, navigate, refetchMyEvents]);

  // Handle search params for tabs - PRIORITY 2 (only if no navigation state)
  useEffect(() => {
    if (!hasNavigationState) {
      const tabParam = searchParams.get('tab');
      if (tabParam && ['dashboard', 'events', 'my-events', 'profile'].includes(tabParam)) {
        setActiveTab(tabParam);
      }
    }
    // Reset the flag after processing
    setHasNavigationState(false);
  }, [searchParams, hasNavigationState]);

  // Check if we're returning from registration with event details
  useEffect(() => {
    if (location.state?.showEventDetail && location.state?.event) {
      // Transform the event data before showing it
      const transformedEvent = transformEventForDisplay(location.state.event);
      handleScreenNavigation('event-registration', transformedEvent);
      // Clear the state to prevent showing it again on refresh
      navigate(location.pathname + location.search, { replace: true, state: null });
    }
  }, [location.state, navigate]);

  // Fetch user profile when user is available
  useEffect(() => {
    const fetchUserProfile = async () => {
      if (user) {
        console.log('Fetching profile for user:', user.id, user.email);
        try {
          const { data, error } = await supabase
            .from('profiles')
            .select('full_name, email, avatar_url')
            .eq('user_id', user.id)
            .single();
          
          if (error) {
            console.log('No profile found, using auth user data');
            // Use auth user data as fallback
            const fallbackProfile = {
              full_name: user.user_metadata?.full_name || user.email?.split('@')[0] || 'Usuario',
              email: user.email,
              avatar_url: null
            };
            console.log('Setting fallback profile:', fallbackProfile);
            setUserProfile(fallbackProfile);
          } else {
            console.log('Profile found:', data);
            setUserProfile(data);
          }
        } catch (err) {
          console.error('Error fetching profile:', err);
          // Fallback to auth user data
          const fallbackProfile = {
            full_name: user.user_metadata?.full_name || user.email?.split('@')[0] || 'Usuario',
            email: user.email
          };
          console.log('Setting fallback profile after error:', fallbackProfile);
          setUserProfile(fallbackProfile);
        }
      } else {
        console.log('No user available, clearing profile');
        setUserProfile(null);
      }
    };

    fetchUserProfile();
  }, [user]);

  // Get user's first name for greeting
  const getUserFirstName = () => {
    if (userProfile?.full_name) {
      return userProfile.full_name.split(' ')[0];
    }
    if (user?.user_metadata?.full_name) {
      return user.user_metadata.full_name.split(' ')[0];
    }
    if (user?.email) {
      return user.email.split('@')[0];
    }
    return 'Usuario';
  };

  // Transform event data from database format to EventCard expected format
  const transformEventForDisplay = (event: any) => {
    if (!event) return event;
    
    // Return transformed event with both database fields and expected EventCard fields
    return {
      ...event,
      // ENSURE id is always a string UUID
      id: String(event.id || event.event_id || ''),
      // Map database fields to EventCard expected fields
      date: event.start_date || event.date,
      registrationFee: event.entry_fee || event.registrationFee || 0,
      maxParticipants: event.max_participants || event.maxParticipants || 0,
      participants: event.participants || 0, // Default to 0 since this field doesn't exist in DB yet
      registrationDeadline: event.registration_deadline || event.registrationDeadline,
      organizer: event.organizer_company_name || event.organizer || "Organizador",
      // Normalize image: accept both camelCase and snake_case
      imageUrl: event.imageUrl || event.image_url || undefined,
      image_url: event.imageUrl || event.image_url || undefined,
      // Ensure dates are properly formatted
      startDate: event.start_date || event.startDate,
      endDate: event.end_date || event.endDate,
      // Keep original fields too for backward compatibility
      start_date: event.start_date,
      entry_fee: event.entry_fee,
      max_participants: event.max_participants,
      registration_deadline: event.registration_deadline,
      organizer_company_name: event.organizer_company_name,
    };
  };

  // Mock notification and chat counts
  const notificationCount = 3;
  const chatCount = 2;

  const handleNotificationClick = () => {
    console.log("Navigate to notifications screen");
    setHideNotificationBadge(true);
    // Add current screen to history before navigating
    setNavigationHistory(prev => [...prev, { screen: currentScreen, data: screenData }]);
    setCurrentScreen("notifications");
    setScreenData({ mode: 'notifications' });
  };

  const handleChatClick = () => {
    console.log("Navigate to chats screen");
    // Add current screen to history before navigating
    setNavigationHistory(prev => [...prev, { screen: currentScreen, data: screenData }]);
    setCurrentScreen("notifications");
    setScreenData({ mode: 'chats' });
  };

  const handleScreenNavigation = (screen: string, data?: any) => {
    console.log("Navigating to:", screen, data);
    
    // Handle profile navigation specially - pass returnTo for proper back navigation
    if (screen === 'profile' && data?.userId) {
      navigate(`/profile/${data.userId}`, { 
        state: { 
          returnTo: {
            screen: currentScreen,
            data: screenData
          }
        } 
      });
      return;
    }
    
    // Transform event data when navigating to event registration
    let transformedData = data;
    if (screen === 'event-registration' && data) {
      transformedData = transformEventForDisplay(data);
    }
    
    // Add current screen to history before navigating
    setNavigationHistory(prev => [...prev, { screen: currentScreen, data: screenData }]);
    
    setCurrentScreen(screen);
    setScreenData(transformedData);
  };

  const handleBackToMain = () => {
    setHideNotificationBadge(false);
    setCurrentScreen("main");
    setScreenData(null);
    setNavigationHistory([{ screen: "main", data: null }]);
  };

  // New function to go back using the navigation history
  const handleGoBack = () => {
    // navigationHistory stores the PREVIOUS screens (we push currentScreen before navigating)
    // So the last item is the real screen we should go back to.
    if (navigationHistory.length > 0) {
      const previousScreen = navigationHistory[navigationHistory.length - 1];
      const newHistory = navigationHistory.slice(0, -1);

      setNavigationHistory(newHistory.length > 0 ? newHistory : [{ screen: "main", data: null }]);
      setCurrentScreen(previousScreen.screen);
      setScreenData(previousScreen.data);
      if (previousScreen.screen !== "notifications") {
        setHideNotificationBadge(false);
      }
      return;
    }

    // Safety fallback
    setCurrentScreen("main");
    setScreenData(null);
    setNavigationHistory([{ screen: "main", data: null }]);
  };

  // New function to handle tab clicks with reset functionality
  const handleTabClick = (tabId: string) => {
    if (activeTab === tabId && currentScreen !== "main") {
      // If clicking on the same tab and not on main screen, reset to main
      handleBackToMain();
    } else {
      // Normal tab navigation
      setActiveTab(tabId);
      if (currentScreen !== "main") {
        handleBackToMain();
      }
    }
  };

  const handleSportChange = (sport: string) => {
    console.log("Sport changed to:", sport);
    setSelectedSport(sport);
  };


  // Real events data from Supabase
  const allEvents = events;

  // Real clubs data from Supabase
  const allClubs = clubs;


  const partnerRequests = [
    {
      id: 1,
      playerName: "María González",
      sport: "Pádel",
      level: "Intermedio",
      preferredTime: "Mañanas",
      location: "Zona Norte",
      avatar: "MG"
    },
    {
      id: 2,
      playerName: "Carlos Ruiz",
      sport: "Tenis",
      level: "Avanzado",
      preferredTime: "Tardes",
      location: "Centro",
      avatar: "CR"
    }
  ];


  // Use real user events from registrations only
  const allMyEvents = myEvents || {
    upcoming: [],
    active: [],
    finished: []
  };
  
  console.log('myEvents from hook:', myEvents);
  console.log('allMyEvents (final):', allMyEvents);

  // Filter myEvents by selected sport and event type
  // Memoized filtering to ensure React detects changes when showTournaments changes
  const filteredMyEvents = useMemo(() => {
    const sportMapping = {
      'padel': 'Pádel',
      'tenis': 'Tenis',
      'pickleball': 'Pickleball'
    };
    
    const selectedSportName = sportMapping[selectedSport as keyof typeof sportMapping];
    console.log('selectedSport:', selectedSport, 'selectedSportName:', selectedSportName);
    console.log('allMyEvents:', allMyEvents);
    console.log('showTournaments:', showTournaments);
    
    // Only filter by sport in Home tab, other tabs show all sports
    const shouldFilterBySport = activeTab === "dashboard";
    
    // First filter by sport (only in Home tab)
    const sportFiltered = {
      upcoming: shouldFilterBySport ? allMyEvents.upcoming.filter((event: any) => event.sport === selectedSportName) : allMyEvents.upcoming,
      active: shouldFilterBySport ? allMyEvents.active.filter((event: any) => event.sport === selectedSportName) : allMyEvents.active,
      finished: shouldFilterBySport ? allMyEvents.finished.filter((event: any) => event.sport === selectedSportName) : allMyEvents.finished
    };
    
    // Then filter by event type (true = tournaments, false = leagues)
    const applyEventTypeFilter = (events: any[]) => {
      if (showTournaments) {
        return events.filter((event: any) => !event.isLeague);
      } else {
        return events.filter((event: any) => event.isLeague);
      }
    };
    
    const filtered = {
      upcoming: applyEventTypeFilter(sportFiltered.upcoming),
      active: applyEventTypeFilter(sportFiltered.active),
      finished: applyEventTypeFilter(sportFiltered.finished)
    };
    
    console.log('filteredMyEvents:', filtered);
    
    return filtered;
  }, [allMyEvents, selectedSport, activeTab, showTournaments]);


  // Transform Supabase organizer data to expected format (featured only - for dashboard)
  const featuredOrganizersData = organizers?.map(org => ({
    id: org.id,
    organizer_id: org.organizer_id, // Include organizer_id for profile navigation
    name: org.company_name || `Organizador ${org.id.slice(0, 8)}`,
    rating: Number(org.rating || 0),
    eventsPerYear: org.events_per_year || 0,
    upcomingEvents: (org as any).upcomingEvents || 0,
    avatar: org.avatar_url,
    sports: org.sports || []
  })) || [];

  // Transform ALL organizers for Explore section
  const allOrganizersData = allOrganizers?.map(org => ({
    id: org.id,
    organizer_id: org.organizer_id, // Include organizer_id for profile navigation
    name: org.company_name || `Organizador ${org.id.slice(0, 8)}`,
    rating: Number(org.rating || 0),
    eventsPerYear: org.events_per_year || 0,
    upcomingEvents: (org as any).upcomingEvents || 0,
    avatar: org.avatar_url,
    sports: org.sports?.length > 0 ? org.sports : []
  })) || [];



  const handleOrganizerClick = (organizer: any) => {
    console.log("Navigate to organizer profile:", organizer);
    // Pass the organizer_id from organizer_public_profiles to fetch real data
    handleScreenNavigation("organizer-profile", { 
      organizerId: organizer.organizer_id || organizer.id
    });
  };

  // New function to handle stats card clicks
  const handleStatsCardClick = (targetTab: string) => {
    setActiveTab(targetTab);
    if (currentScreen !== "main") {
      handleBackToMain();
    }
  };

  // New function to handle "Ver todos" from featured events section
  const handleExploreEvents = () => {
    setActiveTab("events");
    setExploreTab("events");
    if (currentScreen !== "main") {
      handleBackToMain();
    }
  };

  // New function to handle "Ver todos" from featured organizers section
  const handleExploreOrganizers = () => {
    setActiveTab("events");
    setExploreTab("organizers");
    if (currentScreen !== "main") {
      handleBackToMain();
    }
    // Scroll to top after navigation
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Show loading while checking auth, events, clubs, and organizers
  if (authLoading || eventsLoading || clubsLoading || organizersLoading || myEventsLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#1b3521] via-[#31633f] to-[#6da079] flex items-center justify-center">
        <Card className="w-full max-w-md mx-auto">
          <CardContent className="p-8 text-center">
            <div className="animate-spin h-8 w-8 border-4 border-[#3D6B4F] border-t-transparent rounded-full mx-auto mb-4"></div>
            <p className="text-gray-600">Cargando...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Show authentication screen if user is not authenticated
  if (!user) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#1b3521] via-[#31633f] to-[#6da079] flex items-center justify-center p-4">
        <EmbeddedAuth />
      </div>
    );
  }

  // Show database populator if user is authenticated but no events exist (from real DB)
  if (user && events.length === 0) {
    return (
        <div className="min-h-screen bg-gradient-to-br from-[#1b3521] via-[#31633f] to-[#6da079] pb-14 md:pb-16">
        <PlayerNav 
          activeTab={activeTab} 
          setActiveTab={setActiveTab}
          notificationCount={notificationCount}
          chatCount={chatCount}
          onNotificationClick={handleNotificationClick}
          onChatClick={handleChatClick}
          onTabClick={handleTabClick}
          hideNotificationBadge={hideNotificationBadge}
        />
        
        <main className="container mx-auto px-2 md:px-4 py-4 md:py-8">
          <div className="flex items-center justify-center min-h-[60vh]">
            <DatabasePopulator />
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#1b3521] via-[#31633f] to-[#6da079] pb-14 md:pb-16">
      <PlayerNav 
        activeTab={activeTab} 
        setActiveTab={setActiveTab}
        notificationCount={notificationCount}
        chatCount={chatCount}
        onNotificationClick={handleNotificationClick}
        onChatClick={handleChatClick}
        onTabClick={handleTabClick}
        hideNotificationBadge={hideNotificationBadge}
      />
      
      {/* Chat screen ocupa espacio completo entre header y nav */}
      {currentScreen === "chat-screen" && (
        <div className="h-[calc(100vh-120px)] md:h-[calc(100vh-130px)]">
          <ChatScreen
            chatId={screenData?.chatId}
            partnerName={screenData?.partnerName}
            partnerAvatar={screenData?.partnerAvatar}
            partnerId={screenData?.partnerId}
            onBack={handleGoBack}
            onViewProfile={(userId) => handleScreenNavigation("profile", { userId })}
          />
        </div>
      )}
      
      {currentScreen !== "chat-screen" && (
      <main className={currentScreen === "event-registration" ? "" : "container mx-auto px-2 md:px-4 py-4 md:py-8"}>
        {currentScreen === "notifications" && (
          <NotificationsAndChats 
            onNavigate={handleScreenNavigation} 
            mode={screenData?.mode || 'notifications'}
          />
        )}

        {currentScreen === "partner-invitation-confirmation" && (
          <PartnerInvitationConfirmation
            invitationData={screenData}
            onReject={() => {
              console.log("Partner invitation rejected");
              handleBackToMain();
            }}
            onBack={handleBackToMain}
            invitationId="mock-invitation-id"
          />
        )}

        {currentScreen === "event-registration" && (
          <EventRegistrationForm
            event={screenData}
            onContactOrganizer={(organizerData) => handleScreenNavigation("chat-screen", organizerData)}
            onRegister={(registrationData) => {
              console.log("Continue to payment:", registrationData);
              handleScreenNavigation("payment-process", { ...screenData, ...registrationData });
            }}
            onBack={handleBackToMain}
          />
        )}

        {currentScreen === "payment-process" && (
          <PaymentProcess
            paymentData={screenData}
            eventTitle={screenData.title}
            onPaymentSuccess={() => {
              console.log("Payment successful");
              handleBackToMain();
            }}
            onPaymentError={(error) => {
              console.log("Payment error:", error);
            }}
            onBack={() => handleScreenNavigation("event-registration", screenData)}
          />
        )}

        {currentScreen === "organizer-profile" && screenData?.organizerId && (
          <OrganizerProfile
            organizerId={screenData.organizerId}
            onBack={handleBackToMain}
            onEventClick={(event) => handleScreenNavigation("event-registration", event)}
          />
        )}

        {currentScreen === "main" && (
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            
          {/* Home */}
          <TabsContent value="dashboard" className="space-y-4 md:space-y-6">
            <div className="text-center space-y-3 md:space-y-4">
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3 flex-1">
                <Avatar className="h-10 w-10 md:h-12 md:w-12 border-2 shadow-sm shrink-0" style={{ borderColor: '#e7d1b6' }}>
                  <AvatarImage src={userProfile?.avatar_url || ''} alt={userProfile?.full_name || ''} />
                  <AvatarFallback className="bg-gradient-to-br from-primary to-primary/70 text-primary-foreground text-sm md:text-base font-semibold">
                    {(getUserFirstName() || 'U').charAt(0).toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <h1 className="text-lg md:text-3xl font-bold" style={{ color: '#e7d1b6' }}>¡Hola, {user ? getUserFirstName() : 'Usuario'}!</h1>
                  <p className="text-xs md:text-sm mt-0.5" style={{ color: '#e7d1b6', opacity: 0.7 }}>Descubre eventos y conecta con otros jugadores</p>
                  
                  {/* Temporary Migration Button - only show if no real events */}
                  {events.length === 0 && (
                    <Button 
                      onClick={async () => {
                        try {
                          const { data, error } = await supabase.functions.invoke('populate-db');
                          if (error) throw error;
                          alert('¡Datos migrados exitosamente! Refresca la página.');
                          window.location.reload();
                        } catch (err: any) {
                          alert('Error al migrar datos: ' + err.message);
                        }
                      }}
                      className="mt-4 bg-green-600 hover:bg-green-700 text-white"
                    >
                      🔄 Migrar Datos Mock a Base de Datos
                    </Button>
                  )}
                </div>
              </div>
              
              {/* Sport Selector - Now aligned with greeting */}
              <div className="ml-2 md:ml-4 flex flex-col items-center">
                <span className="text-[10px] md:text-xs mb-1" style={{ color: '#e7d1b6', opacity: 0.7 }}>Elige tu deporte</span>
                <SportSelector
                  selectedSport={selectedSport}
                  onSportChange={handleSportChange}
                />
              </div>
            </div>

            {/* Stats Cards - Updated with click functionality */}
            <div className="grid grid-cols-4 gap-1 md:gap-4 items-stretch">
              <Card className="bg-white border-emerald-200 h-full">
                <CardContent className="p-1.5 md:p-4 flex flex-col h-full">
                  <div className="flex flex-col items-center md:items-start flex-grow">
                    <Calendar className="h-3 w-3 md:h-5 md:w-5 text-[#3D6B4F] mb-1 md:mb-0" />
                    <p className="text-[8px] md:text-sm font-medium text-gray-600 leading-tight text-center md:text-left">Próximos eventos cerca de ti</p>
                  </div>
                  <p className="text-sm md:text-2xl font-bold text-[#3D6B4F] text-center md:text-left mt-auto">{events.filter(e => {
                    const isFutureEvent = new Date(e.start_date) > new Date();
                    const matchesSport = e.sport.toLowerCase() === selectedSport.toLowerCase();
                    const hasOpenRegistration = !e.registration_deadline || 
                      new Date(e.registration_deadline) >= new Date();
                    return isFutureEvent && matchesSport && hasOpenRegistration;
                  }).length}</p>
                </CardContent>
              </Card>

              <Card 
                className="bg-white border-green-200 cursor-pointer hover:shadow-md transition-shadow h-full"
                onClick={() => {
                  setShowTournaments(true);
                  setMyEventsTab("finished");
                  handleStatsCardClick("my-events");
                }}
              >
                <CardContent className="p-1.5 md:p-4 flex flex-col h-full">
                  <div className="flex flex-col items-center md:items-start flex-grow">
                    <Trophy className="h-3 w-3 md:h-5 md:w-5 text-green-600 mb-1 md:mb-0" />
                    <p className="text-[8px] md:text-sm font-medium text-gray-600 leading-tight text-center md:text-left">Torneos asistidos</p>
                  </div>
                  <p className="text-sm md:text-2xl font-bold text-green-600 text-center md:text-left mt-auto">{tournamentsCount}</p>
                </CardContent>
              </Card>

              <Card 
                className="bg-white border-orange-200 cursor-pointer hover:shadow-md transition-shadow h-full"
                onClick={() => setFriendsDialogOpen(true)}
              >
                <CardContent className="p-1.5 md:p-4 flex flex-col h-full">
                  <div className="flex flex-col items-center md:items-start flex-grow">
                    <Users className="h-3 w-3 md:h-5 md:w-5 text-orange-600 mb-1 md:mb-0" />
                    <p className="text-[8px] md:text-sm font-medium text-gray-600 leading-tight text-center md:text-left">Compañeros</p>
                  </div>
                  <p className="text-sm md:text-2xl font-bold text-orange-600 text-center md:text-left mt-auto">{followingCount}</p>
                </CardContent>
              </Card>

              <Card 
                className="bg-white border-purple-200 cursor-pointer hover:shadow-md transition-shadow h-full"
                onClick={() => handleStatsCardClick("my-events")}
              >
                <CardContent className="p-1.5 md:p-4 flex flex-col h-full">
                  <div className="flex flex-col items-center md:items-start flex-grow">
                    <MapPin className="h-3 w-3 md:h-5 md:w-5 text-purple-600 mb-1 md:mb-0" />
                    <p className="text-[8px] md:text-sm font-medium text-gray-600 leading-tight text-center md:text-left">Tus próximos eventos</p>
                  </div>
                  <p className="text-sm md:text-2xl font-bold text-purple-600 text-center md:text-left mt-auto">{allMyEvents.upcoming.length}</p>
                </CardContent>
              </Card>
            </div>

            {/* Upcoming Event Preview - Show nearest upcoming events (1 league + 1 tournament max) */}
            {(() => {
              const allUpcoming = [...allMyEvents.upcoming, ...allMyEvents.active]
                .sort((a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime());
              
              const nearestLeague = allUpcoming.find(e => e.isLeague);
              const nearestTournament = allUpcoming.find(e => !e.isLeague);
              const upcomingEvents = [nearestLeague, nearestTournament]
                .filter(Boolean)
                .sort((a, b) => new Date(a!.startDate).getTime() - new Date(b!.startDate).getTime()) as typeof allUpcoming;
              
              if (upcomingEvents.length === 0) return null;
              
              return (
                <UpcomingEventPreview 
                  events={upcomingEvents}
                  onEventClick={(evt) => {
                    if (evt.isLeague) {
                      setShowTournaments(false);
                    } else {
                      setShowTournaments(true);
                    }
                    setMyEventsTab(evt.status === 'active' ? 'active' : 'upcoming');
                    handleStatsCardClick("my-events");
                  }}
                />
              );
            })()}

            {/* Featured Events Section - Now using ExploreEvents component with sport filter */}
            <div className="bg-white rounded-lg py-4 md:py-6 px-0 md:px-6 -mx-2 md:mx-0">
              <div className="flex items-center justify-between gap-2 md:gap-8 mb-4 md:mb-6">
                <div>
                  <h2 className="text-sm md:text-2xl font-bold text-gray-900 flex items-center gap-1 md:gap-2">
                    <Trophy className="h-4 w-4 md:h-6 md:w-6 text-orange-600" />
                    Próximos Torneos
                  </h2>
                  <p className="text-xs md:text-sm text-gray-600 mt-1">Torneos disponibles para inscripción</p>
                </div>
                <Button variant="outline" size="sm" onClick={handleExploreEvents} className="flex items-center gap-1 text-xs md:text-sm h-6 md:h-10 px-2 md:px-4">
                  Ver todos
                  <ArrowRight className="h-3 w-3 md:h-4 md:w-4" />
                </Button>
              </div>
              <ExploreEvents 
                onEventClick={(event) => handleScreenNavigation("event-registration", event)}
                showSearchAndFilters={false}
                maxEvents={2}
                externalSelectedSport={selectedSport}
                onlyUpcoming={true}
              />
            </div>

            {/* My Tournaments Section - Shows user's registered events filtered by sport */}
            <SuggestedClubsSection 
              clubs={filteredMyEvents.upcoming} 
              onExploreMore={() => setActiveTab("my-events")}
              title="Tus próximos torneos"
              showAsEvents={true}
            />

            {/* New Featured Organizers Section - Now with selectedSport prop */}
            <FeaturedOrganizersSection 
              organizers={featuredOrganizersData}
              onOrganizerClick={handleOrganizerClick}
              selectedSport={selectedSport}
              onExploreMore={handleExploreOrganizers}
            />

          </TabsContent>

          <TabsContent value="events" className="">
            <Tabs value={exploreTab} onValueChange={setExploreTab} className="w-full">
              <div className="relative overflow-hidden -mx-2 md:-mx-4 -mt-6">
                <img
                  src={exploreHeaderImages[exploreTab]}
                  alt="Explorar"
                  className="w-full h-48 object-cover transition-opacity duration-300"
                />
                <div className="absolute inset-0 bg-black/30" />
                <div className="absolute bottom-0 left-0 right-0 px-2 md:px-4 pb-1">
                  <TabsList className="grid w-full grid-cols-4 text-xs md:text-sm h-8 md:h-10 bg-transparent border-none shadow-none">
                    <TabsTrigger value="events" className="text-[10px] md:text-sm px-1 md:px-3 text-white/70 data-[state=active]:text-white data-[state=active]:bg-white/20 data-[state=active]:shadow-none">Torneos</TabsTrigger>
                    <TabsTrigger value="leagues" className="text-[10px] md:text-sm px-1 md:px-3 text-white/70 data-[state=active]:text-white data-[state=active]:bg-white/20 data-[state=active]:shadow-none">Ligas</TabsTrigger>
                    <TabsTrigger value="organizers" className="text-[10px] md:text-sm px-1 md:px-3 text-white/70 data-[state=active]:text-white data-[state=active]:bg-white/20 data-[state=active]:shadow-none">Organizadores</TabsTrigger>
                    <TabsTrigger value="players" className="text-[10px] md:text-sm px-1 md:px-3 text-white/70 data-[state=active]:text-white data-[state=active]:bg-white/20 data-[state=active]:shadow-none">Jugadores</TabsTrigger>
                  </TabsList>
                </div>
              </div>

              <TabsContent value="events" className="mt-6 px-2 md:px-4">
                <ExploreEvents onEventClick={(event) => handleScreenNavigation("event-registration", event)} />
              </TabsContent>

              <TabsContent value="leagues" className="mt-6 px-2 md:px-4">
                <ExploreLeagues onLeagueClick={(league) => handleScreenNavigation("event-registration", league)} />
              </TabsContent>

              <TabsContent value="organizers" className="mt-6 px-2 md:px-4">
                <ExploreOrganizers 
                  organizers={allOrganizersData}
                  onOrganizerClick={handleOrganizerClick}
                />
              </TabsContent>

              <TabsContent value="players" className="mt-6 px-2 md:px-4">
                <ExplorePlayers />
              </TabsContent>
            </Tabs>
          </TabsContent>

          <TabsContent value="my-events" className="space-y-4 md:space-y-6">
            
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-xl md:text-3xl font-bold" style={{ color: '#e7d1b6' }}>Mis Eventos</h1>
                <p className="text-sm md:text-base mt-1" style={{ color: '#e7d1b6', opacity: 0.7 }}>Gestiona todos los eventos en los que participas</p>
              </div>
              <div className="flex items-center bg-white rounded-full p-0.5 border border-gray-200 shadow-sm">
                <button
                  onClick={() => setShowTournaments(true)}
                  className={`px-2 md:px-3 py-1 md:py-1.5 rounded-full text-xs font-medium transition-all duration-200 ${
                    showTournaments
                      ? 'bg-[#3D6B4F] text-white shadow-md'
                      : 'text-gray-600 hover:text-gray-800'
                  }`}
                >
                  Torneos
                </button>
                <button
                  onClick={() => setShowTournaments(false)}
                  className={`px-2 md:px-3 py-1 md:py-1.5 rounded-full text-xs font-medium transition-all duration-200 ${
                    !showTournaments
                      ? 'bg-[#4A7D5E] text-white shadow-md'
                      : 'text-gray-600 hover:text-gray-800'
                  }`}
                >
                  Ligas
                </button>
              </div>
            </div>

            <Tabs value={myEventsTab} onValueChange={setMyEventsTab} className="w-full">
              <TabsList className="grid w-full grid-cols-3 h-8 md:h-10">
                <TabsTrigger value="upcoming" className="text-xs md:text-sm">Próximos</TabsTrigger>
                <TabsTrigger value="active" className="text-xs md:text-sm">Activos</TabsTrigger>
                <TabsTrigger value="finished" className="text-xs md:text-sm">Pasados</TabsTrigger>
              </TabsList>

              <TabsContent value="upcoming" className="space-y-4 mt-6">
                {filteredMyEvents.upcoming.length > 0 ? (
                  <div className="grid gap-4" key={`upcoming-${showTournaments}`}>
                    {filteredMyEvents.upcoming.map((event) => (
                      <MyEventCardHorizontal key={event.registrationId || event.id} event={event} />
                    ))}
                  </div>
                ) : (
                  <Card className="bg-white">
                    <CardContent className="p-8 md:p-12 text-center">
                      <Calendar className="h-8 w-8 md:h-12 md:w-12 text-gray-400 mx-auto mb-3 md:mb-4" />
                      <h3 className="text-base md:text-lg font-medium text-gray-900 mb-2">No tienes eventos próximos</h3>
                      <p className="text-sm md:text-base text-gray-600 mb-3 md:mb-4">Explora nuevos eventos y torneos para inscribirte</p>
                      <Button onClick={() => setActiveTab("events")} className="bg-[#3D6B4F] hover:bg-[#345D44] text-sm md:text-base px-4 md:px-6 py-2">
                        Explorar Eventos
                      </Button>
                    </CardContent>
                  </Card>
                )}
              </TabsContent>

              <TabsContent value="active" className="space-y-4 mt-6">
              {filteredMyEvents.active.length > 0 ? (
                  <div className="grid gap-4" key={`active-${showTournaments}`}>
                    {filteredMyEvents.active.map((event: any) => (
                      <MyEventCardHorizontal key={event.registrationId || event.id} event={event} />
                    ))}
                  </div>
                ) : (
                  <Card className="bg-white">
                    <CardContent className="p-8 md:p-12 text-center">
                      <Trophy className="h-8 w-8 md:h-12 md:w-12 text-gray-400 mx-auto mb-3 md:mb-4" />
                      <h3 className="text-base md:text-lg font-medium text-gray-900 mb-2">No tienes eventos activos</h3>
                      <p className="text-sm md:text-base text-gray-600">Los eventos en los que estés participando aparecerán aquí</p>
                    </CardContent>
                  </Card>
                )}
              </TabsContent>

              <TabsContent value="finished" className="space-y-4 mt-6">
                {filteredMyEvents.finished.length > 0 ? (
                  <div className="grid gap-4" key={`finished-${showTournaments}`}>
                    {filteredMyEvents.finished.map((event) => (
                      <MyEventCardHorizontal key={event.registrationId || event.id} event={event} />
                    ))}
                  </div>
                ) : (
                  <Card className="bg-white">
                    <CardContent className="p-8 md:p-12 text-center">
                      <Trophy className="h-8 w-8 md:h-12 md:w-12 text-gray-400 mx-auto mb-3 md:mb-4" />
                      <h3 className="text-base md:text-lg font-medium text-gray-900 mb-2">Sin historial de eventos</h3>
                      <p className="text-sm md:text-base text-gray-600">Tus eventos pasados aparecerán aquí una vez que completes algunos torneos</p>
                    </CardContent>
                  </Card>
                )}
              </TabsContent>
            </Tabs>
          </TabsContent>

          <TabsContent value="profile" className="space-y-4 md:space-y-6">
            <ProfileContent />
          </TabsContent>

        </Tabs>
        )}
      </main>
      )}
      <FollowingListDialog open={friendsDialogOpen} onOpenChange={setFriendsDialogOpen} />
    </div>
  );
};

export default Index;
